import React, { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

export interface MenuAction {
  title: string
  icon?: React.ReactElement
  onClick: () => void
  disabled?: boolean
  /**
   * Why a disabled action can't be used (utils/permissionReason.ts). Shown
   * under its name, so the action stays in the menu and says why.
   */
  reason?: string
  color?: string
}

interface MenuActionsButtonProps {
  actions: MenuAction[]
  buttonIcon?: React.ReactElement
  size?: 'small' | 'medium'
  tooltip?: string
  ariaLabel?: string
  /** Colour for the name and icon of every enabled item (the info blue on admin menus). A disabled item keeps its greyed look. */
  itemColor?: string
  sx?: SxProps<Theme>
}

const MenuActionsButton: React.FC<MenuActionsButtonProps> = ({
  actions,
  buttonIcon,
  size = 'small',
  tooltip,
  ariaLabel,
  itemColor,
  sx = {}
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenuClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    setAnchorEl(null)
  }

  const handleActionClick = (action: MenuAction) => (event: React.MouseEvent) => {
    event.stopPropagation()
    // A disabled action can take focus (to read its reason) but never runs.
    if (action.disabled) return
    handleClose()
    action.onClick()
  }

  const button = (
    <IconButton
      size={size}
      onClick={handleClick}
      aria-label={ariaLabel || tooltip}
      sx={{
        p: 0.5,
        m: 0,
        opacity: 0.7,
        '&:hover': { opacity: 1 },
        ...sx
      }}
    >
      {buttonIcon || <MoreVertIcon fontSize="small" />}
    </IconButton>
  )

  return (
    <>
      {tooltip ? (
        <Tooltip title={tooltip} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        // A disabled action keeps its place in the arrow-key order, so a
        // keyboard or screen-reader user reaches its reason.
        MenuListProps={{ disabledItemsFocusable: true }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {actions.map((action, index) => {
          const reason = action.disabled ? action.reason : undefined
          return (
            <MenuItem
              key={index}
              onClick={handleActionClick(action)}
              disabled={action.disabled}
              // Greyed name and icon, but the reason at full strength so it
              // can be read (MUI dims the whole disabled item otherwise).
              sx={reason ? { alignItems: 'flex-start', '&.Mui-disabled': { opacity: 1 } } : itemColor && !action.disabled ? { color: itemColor } : undefined}
            >
              {action.icon && (
                <ListItemIcon sx={reason ? { color: 'text.disabled', mt: 0.5 } : { color: action.color || 'inherit' }}>
                  {action.icon}
                </ListItemIcon>
              )}
              {reason ? (
                <ListItemText
                  primary={action.title}
                  secondary={reason}
                  primaryTypographyProps={{ color: 'text.disabled' }}
                  secondaryTypographyProps={{ color: 'text.secondary', sx: { whiteSpace: 'normal', maxWidth: 280 } }}
                />
              ) : (
                <ListItemText>{action.title}</ListItemText>
              )}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default MenuActionsButton
